import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import type { Campaign, CampaignPatch, CheckoutPricing } from "../../lib/types";

export const useCampaigns = () =>
	useQuery({
		queryKey: ["campaigns"],
		queryFn: () => api.get<{ items: Campaign[] }>("/api/campaigns"),
	});

export const useCampaign = (campaignId: string | undefined) =>
	useQuery({
		queryKey: ["campaign", campaignId],
		queryFn: () => api.get<{ campaign: Campaign }>(`/api/campaigns/${campaignId}`),
		enabled: Boolean(campaignId),
	});

export const useCreateCampaign = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (patch: CampaignPatch) => api.post<{ campaign: Campaign }>("/api/campaigns", patch),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["campaigns"] }),
	});
};

export const useUpdateCampaign = (campaignId: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (patch: CampaignPatch) =>
			api.put<{ campaign: Campaign }>(`/api/campaigns/${campaignId}`, patch),
		onSuccess: (data) => {
			queryClient.setQueryData(["campaign", campaignId], data);
			void queryClient.invalidateQueries({ queryKey: ["campaigns"] });
		},
	});
};

export const usePublishCampaign = (campaignId: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () =>
			api.post<{
				campaign: Campaign;
				popupsLive: number;
				pricing: CheckoutPricing;
				discount?: string;
			}>(`/api/campaigns/${campaignId}/publish`),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
			void queryClient.invalidateQueries({ queryKey: ["campaigns"] });
		},
	});
};

export const useDeleteCampaign = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (campaignId: string) => api.remove<undefined>(`/api/campaigns/${campaignId}`),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["campaigns"] }),
	});
};
